import * as React from "react"

import { cn } from "@/lib/utils"

// Number fields follow two rules app-wide so a quick entry never becomes "80" or "08":
//  1. Placeholder-as-default — a field holding 0 (or blank) opens empty on focus; the first keystroke IS the value.
//  2. Select-on-focus — a field holding a real number highlights it, so typing replaces instead of appends.
// While focused the typed text is buffered locally (so "0.5" and ".5" survive parent rounding); on blur the
// parent's value is shown again. Callers keep the normal `value` / `onChange(e)` contract.
const NUMBER_CLASS = "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";
const isZeroish = (v) => v === "" || v === null || v === undefined || Number(v) === 0;

const Input = React.forwardRef(({ className, type, onFocus, onBlur, onChange, onMouseUp, inputMode, ...props }, ref) => {
  const numeric = type === "number";
  const controlled = numeric && props.value !== undefined;
  const [buf, setBuf] = React.useState(null); // null = not editing → mirror props.value
  const selectOnMouseUp = React.useRef(false);
  const typed = React.useRef(false);
  const inner = React.useRef(null);
  React.useImperativeHandle(ref, () => inner.current);
  // A value that arrives async while the field sits focused-and-empty (e.g. dialog autofocus) should still show.
  React.useEffect(() => {
    if (controlled && buf === "" && !typed.current && !isZeroish(props.value)) {
      setBuf(String(props.value));
      requestAnimationFrame(() => { if (document.activeElement === inner.current) inner.current.select(); });
    }
  }, [props.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFocus = (e) => {
    typed.current = false;
    if (numeric) {
      if (controlled && isZeroish(props.value)) {
        setBuf("");
      } else {
        if (controlled) setBuf(String(props.value));
        selectOnMouseUp.current = true;
        try { e.target.select(); } catch { /* ignore */ }
      }
    }
    onFocus?.(e);
  };
  const handleMouseUp = (e) => {
    // A click focuses then drops the caret; swallow that first mouseup so the selection survives.
    if (selectOnMouseUp.current) { selectOnMouseUp.current = false; e.preventDefault(); }
    onMouseUp?.(e);
  };
  const handleChange = (e) => {
    typed.current = true;
    if (controlled) setBuf(e.target.value);
    onChange?.(e);
  };
  const handleBlur = (e) => {
    if (controlled) setBuf(null);
    selectOnMouseUp.current = false;
    onBlur?.(e);
  };

  const value = controlled && buf !== null ? buf : props.value;
  return (
    <input
      type={type}
      inputMode={inputMode || (numeric ? "decimal" : undefined)}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        numeric && NUMBER_CLASS,
        className
      )}
      ref={inner}
      onFocus={handleFocus}
      onMouseUp={handleMouseUp}
      onChange={handleChange}
      onBlur={handleBlur}
      {...props}
      {...(controlled ? { value } : {})} />
  );
})
Input.displayName = "Input"

export { Input }
